import { ChangeDetectionStrategy, Component, inject, input, signal, effect, untracked } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { PhonePipe } from '../../pipes/phone.pipe';
import { PhoneInputService } from './phone-input.service';

@Component({
  selector: 'app-phone-input',
  template: `
    <mat-form-field class="w-100">
      <input
        matInput
        type="tel"
        [ngModel]="displayValue()"
        (ngModelChange)="onInputChanged($event)"
        [disabled]="isDisabled()"
        [placeholder]="phonenumberPlaceholder()"
        title="Phonenumber: (999) 999-9999"
      />
    </mat-form-field>
  `,
  standalone: true,
  imports: [FormsModule, MatInputModule, MatFormFieldModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent {
  private readonly phoneService = inject(PhoneInputService);
  private readonly phonePipe = new PhonePipe();

  // Signal inputs
  readonly phoneNumber = input('');
  readonly isDisabled = input(false);
  readonly phonenumberPlaceholder = input('');

  // Internal state for formatted display value
  readonly displayValue = signal('');

  // Track the last propagated value to avoid duplicate emissions
  private lastPropagatedValue = '';

  constructor() {
    // Effect to format phone number when input changes from parent
    effect(() => {
      const inputValue = this.phoneNumber();
      // Use untracked to avoid circular updates
      untracked(() => {
        if (inputValue) {
          const formatted = this.formatPhoneNumber(inputValue);
          this.displayValue.set(formatted);
        } else {
          this.displayValue.set('');
          this.lastPropagatedValue = '';
        }
      });
    });
  }

  onInputChanged(value: string): void {
    if (!value) {
      this.displayValue.set('');
      this.lastPropagatedValue = '';
      return;
    }

    const stripped = value.replace(/[^0-9]+/g, '');
    const formatted = this.phonePipe.transform(stripped);
    this.displayValue.set(formatted);

    // Only propagate when we have a valid phone number (10+ digits)
    if (stripped.length >= 10 && stripped !== this.lastPropagatedValue) {
      this.lastPropagatedValue = stripped;
      this.phoneService.propagateChange(stripped);
    }
  }

  private formatPhoneNumber(phoneNumber: string): string {
    const stripped = phoneNumber.replace(/[^0-9]+/g, '');
    const formatted = this.phonePipe.transform(stripped);

    // Propagate initial value if valid
    if (stripped.length >= 10 && stripped !== this.lastPropagatedValue) {
      this.lastPropagatedValue = stripped;
      this.phoneService.propagateChange(stripped);
    }

    return formatted;
  }
}
